﻿import React, { useEffect, useState } from "react";
import { Box, TextField, Autocomplete, Typography, Paper } from "@mui/material";
import {
  ShepCheckbox,
  ShepSearchBar,
  ShepTab,
  ShepTabs,
  ShepHorizontalMenu,
  ShepGridWarning,
} from "@/components/GenericComponents/ShepComponents";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConnectionPairs,
  selectConnectionPairDetails,
} from "@/features/maintenance/maintenanceSlice";
import {
  selectCrossReferenceSummarySelectedTab,
  setCrossReferenceSummarySelectedTab,
  selectSelectedConnectionPair,
  setSelectedConnectionPair,
  selectCrossReferenceSummaryRecords,
  fetchCrossReferenceSummaries,
  clearAllRecordCaches,
  clearTabCache,
} from "@/features/maintenance/maintenanceSlice";
import DownIcon from "@/assets/img/icons/Down/Line.svg?react";
import "./Maintenance.css";
import {
  setIsAddCrossRefOpen,
  setMappedGridFlag,
} from "@/features/connections/crossReferenceSlice";
import { createAlert } from "@/features/homepage/alertSliceList";
import axios from "@/features/axiosHelper";
import { deleteCrossReferenceMapping } from "@/features/connections/crossReferenceSlice";
import {
  importTabTypesEnum,
  setFileImportPopupOpen,
} from "@/features/maintenance/fileImportSlice";

const CrossReferenceGridToolbar = () => {
  const apiRef = useGridApiContext();
  const dispatch = useDispatch();
  const connectionPairDetails = useSelector(selectConnectionPairDetails);
  const selectedTab = useSelector(selectCrossReferenceSummarySelectedTab);
  const selectedConnectionPair = useSelector(selectSelectedConnectionPair);
  const crossReferenceSummaryRecords = useSelector(
    selectCrossReferenceSummaryRecords
  );
  const [searchText, setSearchText] = useState("");

  const handleCrossReferenceGridTypeTabChange = (event, newValue) => {
    dispatch(setMappedGridFlag({ mappedGridLoaded: false }));
    if (selectedTab !== newValue) {
      dispatch(setCrossReferenceSummarySelectedTab({ selectedTab: newValue }));

      if (selectedConnectionPair && selectedConnectionPair.id) {
        dispatch(
          fetchCrossReferenceSummaries({
            connectionPairId: selectedConnectionPair.id,
            searchText: searchText.trim(),
            crossReferenceType: newValue,
          })
        )
      }
    }

    // heap.io: Trigger when tab is selected by user input

    const tabNames = [
      "Customer",
      "Item",
      "Item UOM",
      "Warehouse",
      "Ship To",
      "Carrier",
      "Allowance/Charge",
      "Tax Group",
    ];

    if (event !== null && newValue < tabNames.length) {
      const heapEventProps = {
        "Cross Reference Type": tabNames[newValue],
      };

      window.heap.track("Cross References - Click Subtab", heapEventProps);
    }
  };

  const handleConnectionPairChange = (event, newValue) => {
    //clear cache since connection pair has changed
    dispatch(clearAllRecordCaches());
    if (newValue === null) {
      dispatch(setSelectedConnectionPair({ selectedConnectionPair: {} }));
      return;
    }
    if (selectedConnectionPair !== newValue) {
      dispatch(setSelectedConnectionPair({ selectedConnectionPair: newValue }));

      // Trigger fetchCrossReferenceSummaries with the selected connection pair ID
      dispatch(
        fetchCrossReferenceSummaries({
          connectionPairId: newValue.ConnectionPairId,
          searchText: searchText.trim(),
          crossReferenceType: selectedTab,
        })
      );

      // heap.io
      window.heap.track("Cross References - Select Connection Pair");
    }
  };

  const handleExport = async () => {
    if (!validateSelected()) {
      dispatch(
        createAlert({
          status: "Error",
          message:
            "Please select a connection pair before attempting to export.",
        })
      );
      return;
    }
    const selectedRows = apiRef.current.getSelectedRows();
    const selectedRowIds = Array.from(selectedRows.values()).map(
      (row) => row.CrossReferenceMappingId
    );

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const crossReferenceType = [
      "Customer",
      "Item",
      "Item UOM",
      "Warehouse",
      "Ship To",
      "Carrier",
      "Allowance/Charge",
      "Tax Group",
    ][selectedTab];
    const filename = `cross_reference_data_${crossReferenceType}_${timestamp}.csv`;

    try {
      const response = await axios.get(
        "/crossreference/export-cross-reference-data",
        {
          params: {
            crossReferenceType: selectedTab,
            selectedRowIds: selectedRowIds.join(","),
            connectionPairId: selectedConnectionPair.ConnectionPairId,
            searchText: searchText.trim(),
          },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // heap.io
      window.heap.track("Cross References - Export");
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const handleAddCrossRefOpen = () => {
    if (!validateSelected()) {
      dispatch(
        createAlert({
          status: "Error",
          message:
            "Please select a connection pair before attempting to add cross reference.",
        })
      );
      return;
    }
    dispatch(setIsAddCrossRefOpen({ value: true }));
  };

  const validateSelected = () => {
    if (
      Object.keys(selectedConnectionPair).length === 0 ||
      selectedConnectionPair.ConnectionPairId === -1
    ) {
      return false;
    }
    return true;
  };

  const handleDelete = () => {
    const selectedRows = apiRef.current.getSelectedRows();
    if (selectedRows.size === 0) {
      dispatch(
        createAlert({
          status: "Error",
          message: "Please select the cross references to delete first.",
        })
      );
      return;
    }

    const isSingularRow = selectedRows.size === 1;

    const title = isSingularRow ? "This Cross Reference will be deleted" : `The ${selectedRows.size} Selected Cross References will be deleted`;

    const message = isSingularRow ? "Are you sure you want to delete this record?" : "Are you sure you want to delete these records?";

    dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: title,
          message: message,
          buttonLabel: "Yes",
          buttonLabel2: "No",
          onButtonClick: () => deleteCrossReferences({ selectedRows }),
        })
      );
  };

  const deleteCrossReferences = async ({ selectedRows = [] }) => {
    const selectedRowIds = Array.from(selectedRows.values()).map(
      (row) => row.CrossReferenceMappingId
    );

    const cascadingDelete = true;

    try {
      await dispatch(
        deleteCrossReferenceMapping({
          selectedMappings: selectedRowIds,
          crossReferenceType: selectedTab, 
          connectionPairId: selectedConnectionPair.ConnectionPairId,
          cascadingDelete: cascadingDelete,
        })
      ).unwrap();
      dispatch(
        createAlert({
          status: "Success",
          message: "Selected rows deleted successfully.",
        })
      );

      // SHEP-633 must refetch the cross references with the new priorities
      await dispatch(clearTabCache(selectedTab));
      await dispatch(
        fetchCrossReferenceSummaries({
          connectionPairId: selectedConnectionPair.ConnectionPairId,
          crossReferenceType: selectedTab,
        })
      );
      // heap.io
      window.heap.track("Cross References - Delete");
    } catch (error) {
      console.error("Error deleting rows:", error);
      dispatch(
        createAlert({
          status: "Error",
          message: "An error occurred while deleting rows.",
        })
      );
    }
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);

    // heap.io
    window.heap.track("Cross References - Use Search Bar");
  };

  useEffect(() => {
    handleCrossReferenceGridTypeTabChange(null, 0);
    dispatch(fetchConnectionPairs())
      .then((response) => {
        if (response.meta.requestStatus !== "fulfilled") {
          console.error("Failed to fetch connection pairs:", response.error);
        }
      })
      .catch((error) => {
        console.error("Error fetching connection pairs:", error);
      });
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedConnectionPair({ selectedConnectionPair: {} }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedConnectionPair.ConnectionPairId) {
      dispatch(
        fetchCrossReferenceSummaries({
          connectionPairId: selectedConnectionPair.ConnectionPairId,
          searchText: searchText.trim(),
          crossReferenceType: selectedTab,
        })
      );
    }
  }, [searchText, selectedConnectionPair, dispatch]);

  const actions = [
    {
      label: "Import",
      onClick: () =>
        dispatch(
          setFileImportPopupOpen({
            open: true,
            tabType: importTabTypesEnum.crossReference,
            dataType: selectedTab,
          })
        ),
    },
    { label: "Export", onClick: handleExport },
    { label: "Add", onClick: handleAddCrossRefOpen },
    { label: "Delete", onClick: handleDelete, variant: "danger" },
  ];

  return (
    <div sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <ShepTabs
        variant="gridSelector"
        value={selectedTab}
        onChange={handleCrossReferenceGridTypeTabChange}
        sx={{ display: "flex", flexDirection: "row", width: "100%" }}
      >
        <ShepTab
          variant="gridSelector"
          key="0"
          label={"Customer"}
          id="tab-0"
          aria-controls="tabpanel-0"
        />
        <ShepTab
          variant="gridSelector"
          key="1"
          label={"Item"}
          id="tab-1"
          aria-controls="tabpanel-1"
        />
        <ShepTab
          variant="gridSelector"
          key="2"
          label={"Item UOM"}
          id="tab-2"
          aria-controls="tabpanel-2"
        />
        <ShepTab
          variant="gridSelector"
          key="3"
          label={"Warehouse"}
          id="tab-3"
          aria-controls="tabpanel-3"
        />
        <ShepTab
          variant="gridSelector"
          key="4"
          label={"Ship To"}
          id="tab-4"
          aria-controls="tabpanel-4"
        />
        <ShepTab
          variant="gridSelector"
          key="5"
          label={"Carrier"}
          id="tab-5"
          aria-controls="tabpanel-5"
        />
        <ShepTab
          variant="gridSelector"
          key="6"
          label={"Allowance/Charge"}
          id="tab-6"
          aria-controls="tabpanel-6"
        />
        <ShepTab
          variant="gridSelector"
          key="7"
          label={"Tax Group"}
          id="tab-7"
          aria-controls="tabpanel-7"
        />
      </ShepTabs>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "25px 0",
          backgroundColor: "var(--disabled)",
        }}
      >
        <Autocomplete
          sx={{ ".MuiInputBase-input": { height: "0px" } }}
          popupIcon={<DownIcon style={{ width: 16, height: 16 }} />}
          disablePortal={true}
          options={connectionPairDetails}
          getOptionLabel={(option) => option.ConnectionPairName}
          isOptionEqualToValue={(option, value) =>
            option.ConnectionPairId === value.ConnectionPairId
          }
          onChange={handleConnectionPairChange}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <ShepCheckbox checked={selected} />
                {option.ConnectionPairName}
              </li>
            );
          }}
          style={{ width: 600 }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Connection Pair" />
          )}
          clearOnEscape={false}
          clearOnBlur={false}
        />
        <ShepSearchBar
          placeholder="Search"
          sx={{ width: 600, marginLeft: "20px" }}
          value={searchText}
          onChange={handleSearchChange}
        />
        <Box sx={{ marginLeft: "auto", display: "flex" }}>
          <ShepHorizontalMenu actions={actions} />
        </Box>
      </div>
      {Object.keys(selectedConnectionPair).length === 0 && (
        <ShepGridWarning
          title="No Connection Pair Selected."
          message="Select a connection pair to display the data table."
          sx={{ minHeight: "100px" }}
        />
      )}
      <SegmentedGridHeader
        apiRef={apiRef}
        selectedConnectionPair={selectedConnectionPair}
      />
    </div>
  );
};

const SegmentedGridHeader = ({ apiRef, selectedConnectionPair }) => {
  const getColumnWidths = () => {
    const columns = apiRef.current.getAllColumns();

    const firstSetColumns = columns.filter(
      (column) =>
        column.headerSegment === 1 || column.headerName === "Checkbox selection"
    );
    const secondSetColumns = columns.filter(
      (column) => column.headerSegment === 2
    );

    const sumWidths = (cols) => {
      return cols.reduce(
        (sum, column) => sum + (column.computedWidth || column.width),
        0
      );
    };

    return {
      firstSetWidth: sumWidths(firstSetColumns),
      secondSetWidth: sumWidths(secondSetColumns),
    };
  };

  const { firstSetWidth, secondSetWidth } = getColumnWidths();

  return (
    <div className="segmentedGridHeader">
      <div
        className="gridHeaderSegment"
        style={{
          minWidth: `${firstSetWidth}px`,
          maxWidth: `${firstSetWidth}px`,
          paddingLeft: "55px",
        }}
      >
        <Typography variant="h4">
          {selectedConnectionPair?.ConnectionName1 || ""}
        </Typography>
      </div>
      <div
        className="gridHeaderSegment"
        style={{
          minWidth: `${secondSetWidth}px`,
          maxWidth: `${secondSetWidth}px`,
        }}
      >
        <Typography variant="h4">
          {selectedConnectionPair?.ConnectionName2 || ""}
        </Typography>
      </div>
      <div className="gridHeaderSegment gridHeaderEndingSegment">
        <Typography variant="h4">{""}</Typography>
      </div>
    </div>
  );
};

export default CrossReferenceGridToolbar;
